import * as i0 from '@angular/core';
import { Injectable, Directive, Input, EventEmitter, Component, HostBinding, Output, HostListener, NgModule, InjectionToken } from '@angular/core';
import { ReplaySubject, Subject, of } from 'rxjs';
import { __decorate } from 'tslib';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { TemplatePortal, PortalInjector, ComponentPortal } from '@angular/cdk/portal';
import { takeUntil, tap, delay, startWith, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import * as i1$1 from '@angular/cdk/overlay';

class Destructible {
    constructor() {
        this.ends = new ReplaySubject(1);
    }
    ngOnDestroy() {
        this.ends.next();
        this.ends.complete();
    }
}
Destructible.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: Destructible, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
Destructible.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: Destructible });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: Destructible, decorators: [{
            type: Injectable
        }] });

const INPUTS_LIST_NAME = '__pdi-input-documentation__';
const OUTPUTS_LIST_NAME = '__pdi-output-documentation__';
const HIERARCHY_MAP = '__pdi-component-hierarchy-documentation__';
class PdiDocumentationContainer {
}
function DocumentComponent(documentation) {
    if (PdiDocumentationContainer.prototype[HIERARCHY_MAP] === undefined) {
        PdiDocumentationContainer.prototype[HIERARCHY_MAP] = new Map();
    }
    const hierarchyMap = PdiDocumentationContainer.prototype[HIERARCHY_MAP];
    hierarchyMap.set(documentation.constructor.name, documentation.inherits ?? []);
}
function DocumentInput(documentation) {
    if (PdiDocumentationContainer.prototype[INPUTS_LIST_NAME] === undefined) {
        PdiDocumentationContainer.prototype[INPUTS_LIST_NAME] = [];
    }
    PdiDocumentationContainer.prototype[INPUTS_LIST_NAME].push(documentation);
}
function DocumentOutput(documentation) {
    if (PdiDocumentationContainer.prototype[OUTPUTS_LIST_NAME] === undefined) {
        PdiDocumentationContainer.prototype[OUTPUTS_LIST_NAME] = [];
    }
    PdiDocumentationContainer.prototype[OUTPUTS_LIST_NAME].push(documentation);
}

// tslint:disable-next-line:directive-class-suffix
class Themable extends Destructible {
    constructor() {
        super(...arguments);
        this._pdiTheme = 'default';
    }
    /**
     * Defines color theme for the library element. Default is <b>default</b>
     */
    set pdiTheme(theme) {
        this._pdiTheme = theme;
        this.themeSet(this._pdiTheme);
    }
    get pdiTheme() {
        return this._pdiTheme;
    }
    themeSet(theme) {
        const element = this.retrieveElementRef()?.nativeElement;
        if (element) {
            element.classList.forEach((value, key, parent) => {
                if (value.startsWith('pdi-theme')) {
                    parent.remove(value);
                }
            });
            element.classList.add(`pdi-theme-${theme}`);
        }
    }
}
Themable.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: Themable, deps: null, target: i0.ɵɵFactoryTarget.Directive });
Themable.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.1.3", type: Themable, inputs: { pdiTheme: "pdiTheme" }, usesInheritance: true, ngImport: i0 });
__decorate([
    DocumentInput({
        constructor: Themable,
        inherited: true,
        name: 'pdiTheme',
        type: 'single-select',
        options: ['default', 'inverse'],
        arrayType: 'string',
        description: 'Defines color theme for the library element. Default is <b>default</b>',
    })
], Themable.prototype, "pdiTheme", null);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: Themable, decorators: [{
            type: Directive
        }], propDecorators: { pdiTheme: [{
                type: Input
            }] } });

class PdiCoreCheckmarkComponent {
    constructor() {
        /**
         * Flag indicating whether the checkmark is selected. Default is <b>false</b>.
         */
        this.checked = false;
        /**
         * Flag indicating whether the checkmark is in an intermediate state. If true, <i>checked</i> value is ignored. Default is <b>false</b>.
         */
        this.intermediate = false;
        /**
         * Flag indicating whether the component is in read only UI state.
         */
        this.readOnly = false;
        /**
         * Flag indicating whether the component is in disabled UI state
         */
        this.isDisabled = false;
        /**
         * Flag indicating whether the component should use the 'focused' UI state. Useful for cases when focus is maintaned by the parente of the component. Default is <b>false</b>
         */
        this.isFocused = false;
        /**
         * Flag indicating whether the component has tabindex attribtue set and this whether it is focusable using keyboard. Default is <b>true</b>.
         */
        this.isFocusable = true;
        /**
         * Output stream firing a boolean value indicating whether the checkmark is selected on every selection change.
         */
        this.checkmarkInteraction$ = new EventEmitter();
    }
    get tabindex() {
        return (this.isFocusable && !this.isDisabled && !this.readOnly) ? 0 : -1;
    }
    clicked() {
        this.checked = !this.checked;
        document.activeElement.blur();
        this.checkmarkInteraction$.emit(this.checked);
    }
    enter() {
        if (!this.isDisabled && !this.readOnly) {
            this.checked = !this.checked;
            this.checkmarkInteraction$.emit(this.checked);
        }
    }
}
PdiCoreCheckmarkComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiCoreCheckmarkComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
PdiCoreCheckmarkComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.1.3", type: PdiCoreCheckmarkComponent, selector: "pdi-core-checkmark", inputs: { checked: "checked", intermediate: "intermediate", readOnly: "readOnly", isDisabled: "isDisabled", isFocused: "isFocused", isFocusable: "isFocusable" }, outputs: { checkmarkInteraction$: "checkmarkInteraction$" }, host: { listeners: { "click": "clicked()", "keydown.enter": "enter()" }, properties: { "class.pdi-checked": "this.checked", "class.pdi-intermediate": "this.intermediate", "class.pdi-read-only": "this.readOnly", "class.pdi-disabled": "this.isDisabled", "class.pdi-focused": "this.isFocused", "attr.tabindex": "this.tabindex" } }, ngImport: i0, template: "<i *ngIf=\"!intermediate; else inter\" class=\"pdi-icon-Checkmark\"></i>\r\n<ng-template #inter>\r\n  <i class=\"pdi-icon- pdi-intermediate\"> </i>\r\n</ng-template>\r\n", styles: [".pdi-theme-default-foreground{color:#323e48}.pdi-theme-default-foreground::placeholder{color:inherit!important}.pdi-theme-default-foreground:focus,.pdi-theme-default-foreground:focus-within{color:#a10a7d}.pdi-theme-default-foreground:focus::placeholder,.pdi-theme-default-foreground:focus-within::placeholder{color:inherit!important}.pdi-theme-default-foreground.pdi-invalid{color:#a10a7d}.pdi-theme-default-foreground.pdi-invalid::placeholder{color:inherit!important}.pdi-theme-default-foreground.pdi-disabled{color:#d9d8d6}.pdi-theme-default-foreground.pdi-disabled::placeholder{color:inherit!important}.pdi-theme-default-background{background-color:#fff;border-color:#d9d8d6}.pdi-theme-default-background:focus,.pdi-theme-default-background:focus-within{border-color:#a10a7d}.pdi-theme-default-background:not(.pdi-disabled):hover{box-shadow:0 0 2px #07003d3d}.pdi-theme-default-background.pdi-disabled{background-color:#f7f9fb}.pdi-theme-dark-foreground{color:#323e48}.pdi-theme-dark-foreground::placeholder{color:inherit!important}.pdi-theme-dark-foreground:focus,.pdi-theme-dark-foreground:focus-within{color:#ffb71b}.pdi-theme-dark-foreground:focus::placeholder,.pdi-theme-dark-foreground:focus-within::placeholder{color:inherit!important}.pdi-theme-dark-foreground.pdi-invalid{color:#ffb71b}.pdi-theme-dark-foreground.pdi-invalid::placeholder{color:inherit!important}.pdi-theme-dark-foreground.pdi-disabled{color:#d9d8d6}.pdi-theme-dark-foreground.pdi-disabled::placeholder{color:inherit!important}.pdi-theme-dark-background{background-color:#fff;border-color:#d9d8d6}.pdi-theme-dark-background:focus,.pdi-theme-dark-background:focus-within{border-color:#ffb71b}.pdi-theme-dark-background:not(.pdi-disabled):hover{box-shadow:0 0 2px #07003d3d}.pdi-theme-dark-background.pdi-disabled{background-color:#f7f9fb}:host{width:18px;height:18px;border-radius:4px;display:inline-block;border-style:solid;border-width:1px;display:flex;align-items:center;justify-content:center;-webkit-user-select:none;user-select:none;cursor:pointer;background-color:#fff;border-color:#d9d8d6}:host>i[class^=pdi-icon-]{color:transparent}:host>i[class^=pdi-icon-].pdi-intermediate{background-color:transparent}:host.pdi-focused,:host:focus:not(:active){background-color:#fff;border-color:#a10a7d}:host.pdi-focused>i[class^=pdi-icon-],:host:focus:not(:active)>i[class^=pdi-icon-]{color:transparent}:host.pdi-focused>i[class^=pdi-icon-].pdi-intermediate,:host:focus:not(:active)>i[class^=pdi-icon-].pdi-intermediate{background-color:transparent}:host.pdi-disabled{background-color:#f7f9fb;border-color:#d9d8d6}:host.pdi-disabled>i[class^=pdi-icon-]{color:transparent}:host.pdi-disabled>i[class^=pdi-icon-].pdi-intermediate{background-color:transparent}:host.pdi-disabled.pdi-checked,:host.pdi-disabled.pdi-intermediate{background-color:#d9d8d6;border-color:#d9d8d6}:host.pdi-disabled.pdi-checked>i[class^=pdi-icon-],:host.pdi-disabled.pdi-intermediate>i[class^=pdi-icon-]{color:#f7f9fb}:host.pdi-disabled.pdi-checked>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-disabled.pdi-intermediate>i[class^=pdi-icon-].pdi-intermediate{background-color:#f7f9fb}:host.pdi-read-only{background-color:#f7f9fb;border-color:#d9d8d6}:host.pdi-read-only>i[class^=pdi-icon-]{color:transparent}:host.pdi-read-only>i[class^=pdi-icon-].pdi-intermediate{background-color:transparent}:host.pdi-read-only.pdi-checked,:host.pdi-read-only.pdi-intermediate{background-color:#323e48;border-color:#323e48}:host.pdi-read-only.pdi-checked>i[class^=pdi-icon-],:host.pdi-read-only.pdi-intermediate>i[class^=pdi-icon-]{color:#f7f9fb}:host.pdi-read-only.pdi-checked>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-read-only.pdi-intermediate>i[class^=pdi-icon-].pdi-intermediate{background-color:#f7f9fb}:host.pdi-checked,:host.pdi-intermediate{background-color:#1226aa;border-color:#1226aa}:host.pdi-checked>i[class^=pdi-icon-],:host.pdi-intermediate>i[class^=pdi-icon-]{color:#fff}:host.pdi-checked>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-intermediate>i[class^=pdi-icon-].pdi-intermediate{background-color:#fff}:host.pdi-checked.pdi-focused,:host.pdi-checked:focus,:host.pdi-intermediate.pdi-focused,:host.pdi-intermediate:focus{background-color:#a10a7d;border-color:#a10a7d}:host.pdi-checked.pdi-focused>i[class^=pdi-icon-],:host.pdi-checked:focus>i[class^=pdi-icon-],:host.pdi-intermediate.pdi-focused>i[class^=pdi-icon-],:host.pdi-intermediate:focus>i[class^=pdi-icon-]{color:#fff}:host.pdi-checked.pdi-focused>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-checked:focus>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-intermediate.pdi-focused>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-intermediate:focus>i[class^=pdi-icon-].pdi-intermediate{background-color:#fff}:host.pdi-disabled,:host.pdi-read-only{pointer:initial;pointer-events:none}:host i[class^=pdi-icon-]{font-size:11px;-webkit-user-select:none;user-select:none}:host i[class^=pdi-icon-].pdi-intermediate{height:1px;width:40%}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiCoreCheckmarkComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pdi-core-checkmark', template: "<i *ngIf=\"!intermediate; else inter\" class=\"pdi-icon-Checkmark\"></i>\r\n<ng-template #inter>\r\n  <i class=\"pdi-icon- pdi-intermediate\"> </i>\r\n</ng-template>\r\n", styles: [".pdi-theme-default-foreground{color:#323e48}.pdi-theme-default-foreground::placeholder{color:inherit!important}.pdi-theme-default-foreground:focus,.pdi-theme-default-foreground:focus-within{color:#a10a7d}.pdi-theme-default-foreground:focus::placeholder,.pdi-theme-default-foreground:focus-within::placeholder{color:inherit!important}.pdi-theme-default-foreground.pdi-invalid{color:#a10a7d}.pdi-theme-default-foreground.pdi-invalid::placeholder{color:inherit!important}.pdi-theme-default-foreground.pdi-disabled{color:#d9d8d6}.pdi-theme-default-foreground.pdi-disabled::placeholder{color:inherit!important}.pdi-theme-default-background{background-color:#fff;border-color:#d9d8d6}.pdi-theme-default-background:focus,.pdi-theme-default-background:focus-within{border-color:#a10a7d}.pdi-theme-default-background:not(.pdi-disabled):hover{box-shadow:0 0 2px #07003d3d}.pdi-theme-default-background.pdi-disabled{background-color:#f7f9fb}.pdi-theme-dark-foreground{color:#323e48}.pdi-theme-dark-foreground::placeholder{color:inherit!important}.pdi-theme-dark-foreground:focus,.pdi-theme-dark-foreground:focus-within{color:#ffb71b}.pdi-theme-dark-foreground:focus::placeholder,.pdi-theme-dark-foreground:focus-within::placeholder{color:inherit!important}.pdi-theme-dark-foreground.pdi-invalid{color:#ffb71b}.pdi-theme-dark-foreground.pdi-invalid::placeholder{color:inherit!important}.pdi-theme-dark-foreground.pdi-disabled{color:#d9d8d6}.pdi-theme-dark-foreground.pdi-disabled::placeholder{color:inherit!important}.pdi-theme-dark-background{background-color:#fff;border-color:#d9d8d6}.pdi-theme-dark-background:focus,.pdi-theme-dark-background:focus-within{border-color:#ffb71b}.pdi-theme-dark-background:not(.pdi-disabled):hover{box-shadow:0 0 2px #07003d3d}.pdi-theme-dark-background.pdi-disabled{background-color:#f7f9fb}:host{width:18px;height:18px;border-radius:4px;display:inline-block;border-style:solid;border-width:1px;display:flex;align-items:center;justify-content:center;-webkit-user-select:none;user-select:none;cursor:pointer;background-color:#fff;border-color:#d9d8d6}:host>i[class^=pdi-icon-]{color:transparent}:host>i[class^=pdi-icon-].pdi-intermediate{background-color:transparent}:host.pdi-focused,:host:focus:not(:active){background-color:#fff;border-color:#a10a7d}:host.pdi-focused>i[class^=pdi-icon-],:host:focus:not(:active)>i[class^=pdi-icon-]{color:transparent}:host.pdi-focused>i[class^=pdi-icon-].pdi-intermediate,:host:focus:not(:active)>i[class^=pdi-icon-].pdi-intermediate{background-color:transparent}:host.pdi-disabled{background-color:#f7f9fb;border-color:#d9d8d6}:host.pdi-disabled>i[class^=pdi-icon-]{color:transparent}:host.pdi-disabled>i[class^=pdi-icon-].pdi-intermediate{background-color:transparent}:host.pdi-disabled.pdi-checked,:host.pdi-disabled.pdi-intermediate{background-color:#d9d8d6;border-color:#d9d8d6}:host.pdi-disabled.pdi-checked>i[class^=pdi-icon-],:host.pdi-disabled.pdi-intermediate>i[class^=pdi-icon-]{color:#f7f9fb}:host.pdi-disabled.pdi-checked>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-disabled.pdi-intermediate>i[class^=pdi-icon-].pdi-intermediate{background-color:#f7f9fb}:host.pdi-read-only{background-color:#f7f9fb;border-color:#d9d8d6}:host.pdi-read-only>i[class^=pdi-icon-]{color:transparent}:host.pdi-read-only>i[class^=pdi-icon-].pdi-intermediate{background-color:transparent}:host.pdi-read-only.pdi-checked,:host.pdi-read-only.pdi-intermediate{background-color:#323e48;border-color:#323e48}:host.pdi-read-only.pdi-checked>i[class^=pdi-icon-],:host.pdi-read-only.pdi-intermediate>i[class^=pdi-icon-]{color:#f7f9fb}:host.pdi-read-only.pdi-checked>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-read-only.pdi-intermediate>i[class^=pdi-icon-].pdi-intermediate{background-color:#f7f9fb}:host.pdi-checked,:host.pdi-intermediate{background-color:#1226aa;border-color:#1226aa}:host.pdi-checked>i[class^=pdi-icon-],:host.pdi-intermediate>i[class^=pdi-icon-]{color:#fff}:host.pdi-checked>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-intermediate>i[class^=pdi-icon-].pdi-intermediate{background-color:#fff}:host.pdi-checked.pdi-focused,:host.pdi-checked:focus,:host.pdi-intermediate.pdi-focused,:host.pdi-intermediate:focus{background-color:#a10a7d;border-color:#a10a7d}:host.pdi-checked.pdi-focused>i[class^=pdi-icon-],:host.pdi-checked:focus>i[class^=pdi-icon-],:host.pdi-intermediate.pdi-focused>i[class^=pdi-icon-],:host.pdi-intermediate:focus>i[class^=pdi-icon-]{color:#fff}:host.pdi-checked.pdi-focused>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-checked:focus>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-intermediate.pdi-focused>i[class^=pdi-icon-].pdi-intermediate,:host.pdi-intermediate:focus>i[class^=pdi-icon-].pdi-intermediate{background-color:#fff}:host.pdi-disabled,:host.pdi-read-only{pointer:initial;pointer-events:none}:host i[class^=pdi-icon-]{font-size:11px;-webkit-user-select:none;user-select:none}:host i[class^=pdi-icon-].pdi-intermediate{height:1px;width:40%}\n"] }]
        }], propDecorators: { checked: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-checked']
            }], intermediate: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-intermediate']
            }], readOnly: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-read-only']
            }], isDisabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-disabled']
            }], isFocused: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-focused']
            }], isFocusable: [{
                type: Input
            }], checkmarkInteraction$: [{
                type: Output
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], clicked: [{
                type: HostListener,
                args: ['click']
            }], enter: [{
                type: HostListener,
                args: ['keydown.enter']
            }] } });

class CoreRadioComponent {
    constructor() {
        this.isChecked = false;
        this.isDisabled = false;
        this.isReadOnly = false;
        this.isFocused = false;
        this.isFocusable = true;
        this.radioInteraction$ = new EventEmitter();
    }
    get tabindex() {
        return (this.isFocusable && !this.isDisabled && !this.isReadOnly) ? 0 : -1;
    }
    clicked() {
        // (document.activeElement as HTMLElement).blur();
        this.radioInteraction$.emit();
    }
    enter() {
        if (!this.isDisabled && !this.isReadOnly) {
            this.clicked();
        }
    }
}
CoreRadioComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: CoreRadioComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CoreRadioComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.1.3", type: CoreRadioComponent, selector: "pdi-core-radio", inputs: { isChecked: "isChecked", isDisabled: "isDisabled", isReadOnly: "isReadOnly", isFocused: "isFocused", isFocusable: "isFocusable" }, outputs: { radioInteraction$: "radioInteraction$" }, host: { listeners: { "click": "clicked()", "keydown.enter": "enter()" }, properties: { "class.pdi-checked": "this.isChecked", "class.pdi-disabled": "this.isDisabled", "class.pdi-read-only": "this.isReadOnly", "class.pdi-focused": "this.isFocused", "attr.tabindex": "this.tabindex" } }, ngImport: i0, template: "<div class=\"pdi-core-radio__tick\"></div>\r\n\r\n", styles: [".pdi-theme-default-foreground{color:#323e48}.pdi-theme-default-foreground::placeholder{color:inherit!important}.pdi-theme-default-foreground:focus,.pdi-theme-default-foreground:focus-within{color:#a10a7d}.pdi-theme-default-foreground:focus::placeholder,.pdi-theme-default-foreground:focus-within::placeholder{color:inherit!important}.pdi-theme-default-foreground.pdi-invalid{color:#a10a7d}.pdi-theme-default-foreground.pdi-invalid::placeholder{color:inherit!important}.pdi-theme-default-foreground.pdi-disabled{color:#d9d8d6}.pdi-theme-default-foreground.pdi-disabled::placeholder{color:inherit!important}.pdi-theme-default-background{background-color:#fff;border-color:#d9d8d6}.pdi-theme-default-background:focus,.pdi-theme-default-background:focus-within{border-color:#a10a7d}.pdi-theme-default-background:not(.pdi-disabled):hover{box-shadow:0 0 2px #07003d3d}.pdi-theme-default-background.pdi-disabled{background-color:#f7f9fb}.pdi-theme-dark-foreground{color:#323e48}.pdi-theme-dark-foreground::placeholder{color:inherit!important}.pdi-theme-dark-foreground:focus,.pdi-theme-dark-foreground:focus-within{color:#ffb71b}.pdi-theme-dark-foreground:focus::placeholder,.pdi-theme-dark-foreground:focus-within::placeholder{color:inherit!important}.pdi-theme-dark-foreground.pdi-invalid{color:#ffb71b}.pdi-theme-dark-foreground.pdi-invalid::placeholder{color:inherit!important}.pdi-theme-dark-foreground.pdi-disabled{color:#d9d8d6}.pdi-theme-dark-foreground.pdi-disabled::placeholder{color:inherit!important}.pdi-theme-dark-background{background-color:#fff;border-color:#d9d8d6}.pdi-theme-dark-background:focus,.pdi-theme-dark-background:focus-within{border-color:#ffb71b}.pdi-theme-dark-background:not(.pdi-disabled):hover{box-shadow:0 0 2px #07003d3d}.pdi-theme-dark-background.pdi-disabled{background-color:#f7f9fb}:host{border-color:#d9d8d6;border-radius:50%;display:flex;justify-content:center;align-items:center;height:16px;width:16px;border-style:solid;border-width:1px}:host>div.pdi-core-radio__tick{background-color:transparent}:host.pdi-focused,:host:focus:not(:active){border-color:#a10a7d}:host.pdi-focused>div.pdi-core-radio__tick,:host:focus:not(:active)>div.pdi-core-radio__tick{background-color:transparent}:host.pdi-checked{border-color:#1226aa}:host.pdi-checked>div.pdi-core-radio__tick{background-color:#1226aa}:host.pdi-checked.pdi-focused,:host.pdi-checked:focus:not(:active){border-color:#a10a7d}:host.pdi-checked.pdi-focused>div.pdi-core-radio__tick,:host.pdi-checked:focus:not(:active)>div.pdi-core-radio__tick{background-color:#a10a7d}:host.pdi-disabled{border-color:#d9d8d6}:host.pdi-disabled>div.pdi-core-radio__tick{background-color:transparent}:host.pdi-disabled.pdi-checked{border-color:#d9d8d6}:host.pdi-disabled.pdi-checked>div.pdi-core-radio__tick{background-color:#d9d8d6}:host.pdi-read-only{border-color:#323e48}:host.pdi-read-only>div.pdi-core-radio__tick{background-color:transparent}:host.pdi-read-only.pdi-checked{border-color:#323e48}:host.pdi-read-only.pdi-checked>div.pdi-core-radio__tick{background-color:#323e48}:host>div.pdi-core-radio__tick{border-radius:50%;width:8px;height:8px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: CoreRadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pdi-core-radio', template: "<div class=\"pdi-core-radio__tick\"></div>\r\n\r\n", styles: [".pdi-theme-default-foreground{color:#323e48}.pdi-theme-default-foreground::placeholder{color:inherit!important}.pdi-theme-default-foreground:focus,.pdi-theme-default-foreground:focus-within{color:#a10a7d}.pdi-theme-default-foreground:focus::placeholder,.pdi-theme-default-foreground:focus-within::placeholder{color:inherit!important}.pdi-theme-default-foreground.pdi-invalid{color:#a10a7d}.pdi-theme-default-foreground.pdi-invalid::placeholder{color:inherit!important}.pdi-theme-default-foreground.pdi-disabled{color:#d9d8d6}.pdi-theme-default-foreground.pdi-disabled::placeholder{color:inherit!important}.pdi-theme-default-background{background-color:#fff;border-color:#d9d8d6}.pdi-theme-default-background:focus,.pdi-theme-default-background:focus-within{border-color:#a10a7d}.pdi-theme-default-background:not(.pdi-disabled):hover{box-shadow:0 0 2px #07003d3d}.pdi-theme-default-background.pdi-disabled{background-color:#f7f9fb}.pdi-theme-dark-foreground{color:#323e48}.pdi-theme-dark-foreground::placeholder{color:inherit!important}.pdi-theme-dark-foreground:focus,.pdi-theme-dark-foreground:focus-within{color:#ffb71b}.pdi-theme-dark-foreground:focus::placeholder,.pdi-theme-dark-foreground:focus-within::placeholder{color:inherit!important}.pdi-theme-dark-foreground.pdi-invalid{color:#ffb71b}.pdi-theme-dark-foreground.pdi-invalid::placeholder{color:inherit!important}.pdi-theme-dark-foreground.pdi-disabled{color:#d9d8d6}.pdi-theme-dark-foreground.pdi-disabled::placeholder{color:inherit!important}.pdi-theme-dark-background{background-color:#fff;border-color:#d9d8d6}.pdi-theme-dark-background:focus,.pdi-theme-dark-background:focus-within{border-color:#ffb71b}.pdi-theme-dark-background:not(.pdi-disabled):hover{box-shadow:0 0 2px #07003d3d}.pdi-theme-dark-background.pdi-disabled{background-color:#f7f9fb}:host{border-color:#d9d8d6;border-radius:50%;display:flex;justify-content:center;align-items:center;height:16px;width:16px;border-style:solid;border-width:1px}:host>div.pdi-core-radio__tick{background-color:transparent}:host.pdi-focused,:host:focus:not(:active){border-color:#a10a7d}:host.pdi-focused>div.pdi-core-radio__tick,:host:focus:not(:active)>div.pdi-core-radio__tick{background-color:transparent}:host.pdi-checked{border-color:#1226aa}:host.pdi-checked>div.pdi-core-radio__tick{background-color:#1226aa}:host.pdi-checked.pdi-focused,:host.pdi-checked:focus:not(:active){border-color:#a10a7d}:host.pdi-checked.pdi-focused>div.pdi-core-radio__tick,:host.pdi-checked:focus:not(:active)>div.pdi-core-radio__tick{background-color:#a10a7d}:host.pdi-disabled{border-color:#d9d8d6}:host.pdi-disabled>div.pdi-core-radio__tick{background-color:transparent}:host.pdi-disabled.pdi-checked{border-color:#d9d8d6}:host.pdi-disabled.pdi-checked>div.pdi-core-radio__tick{background-color:#d9d8d6}:host.pdi-read-only{border-color:#323e48}:host.pdi-read-only>div.pdi-core-radio__tick{background-color:transparent}:host.pdi-read-only.pdi-checked{border-color:#323e48}:host.pdi-read-only.pdi-checked>div.pdi-core-radio__tick{background-color:#323e48}:host>div.pdi-core-radio__tick{border-radius:50%;width:8px;height:8px}\n"] }]
        }], propDecorators: { isChecked: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-checked']
            }], isDisabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-disabled']
            }], isReadOnly: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-read-only']
            }], isFocused: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.pdi-focused']
            }], isFocusable: [{
                type: Input
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], radioInteraction$: [{
                type: Output
            }], clicked: [{
                type: HostListener,
                args: ['click']
            }], enter: [{
                type: HostListener,
                args: ['keydown.enter']
            }] } });

class PdiCoreComponentsModule {
}
PdiCoreComponentsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiCoreComponentsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PdiCoreComponentsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.3", ngImport: i0, type: PdiCoreComponentsModule, declarations: [PdiCoreCheckmarkComponent,
        CoreRadioComponent], imports: [CommonModule], exports: [PdiCoreCheckmarkComponent,
        CoreRadioComponent] });
PdiCoreComponentsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiCoreComponentsModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiCoreComponentsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PdiCoreCheckmarkComponent,
                        CoreRadioComponent,
                    ],
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        PdiCoreCheckmarkComponent,
                        CoreRadioComponent,
                    ]
                }]
        }] });

class PdiKeydownEnterDirective {
    constructor(hostRef) {
        this.keydownEnterDisableEnterHandling = false;
        this.keydownEnter = new EventEmitter();
        if (hostRef) {
            const originalTabIndexValue = hostRef.nativeElement.getAttribute('tabindex');
            if (originalTabIndexValue === null || originalTabIndexValue === undefined) {
                hostRef.nativeElement.setAttribute('tabindex', 0);
            }
        }
    }
    enter(event) {
        if (this.keydownEnterDisableEnterHandling) {
            return;
        }
        this.keydownEnter.emit(event);
    }
}
PdiKeydownEnterDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiKeydownEnterDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
PdiKeydownEnterDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.1.3", type: PdiKeydownEnterDirective, selector: "[pdiKeydownEnter]", inputs: { keydownEnterDisableEnterHandling: "keydownEnterDisableEnterHandling" }, outputs: { keydownEnter: "keydownEnter" }, host: { listeners: { "keydown.enter": "enter($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiKeydownEnterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[pdiKeydownEnter]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { keydownEnterDisableEnterHandling: [{
                type: Input
            }], keydownEnter: [{
                type: Output
            }], enter: [{
                type: HostListener,
                args: ['keydown.enter', ['$event']]
            }] } });

class PdiInputModule {
}
PdiInputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PdiInputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.3", ngImport: i0, type: PdiInputModule, declarations: [PdiKeydownEnterDirective], imports: [CommonModule], exports: [PdiKeydownEnterDirective] });
PdiInputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiInputModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PdiKeydownEnterDirective,
                    ],
                    exports: [
                        PdiKeydownEnterDirective,
                    ],
                    imports: [
                        CommonModule
                    ]
                }]
        }] });

var KeyCode;
(function (KeyCode) {
    KeyCode["Space"] = "Space";
    KeyCode["Enter"] = "Enter";
    KeyCode["Tab"] = "Tab";
    KeyCode["Down"] = "ArrowDown";
    KeyCode["Up"] = "ArrowUp";
})(KeyCode || (KeyCode = {}));

const OVERLAY_DATA = new InjectionToken('PDI_OVERLAY_DATA_INJECTION_TOKEN');

function exists(target) {
    return target !== undefined && target !== null;
}

class PdiOverlayDirective extends Destructible {
    constructor(overlay, injector, elementRef, viewContainerRef) {
        super();
        this.overlay = overlay;
        this.injector = injector;
        this.elementRef = elementRef;
        this.viewContainerRef = viewContainerRef;
        /**
         * Flag indicating whether hitting the space key will not trigger the overlay to open. Default is <b>false</b>.
         */
        this.disableSpaceOverlayTrigger = false;
        /**
         * Flag indicating whether to shift focus to the overlay if it is open and user presses Tab key. Default is <b>true</b>
         */
        this.pdiOverlayFocusOnTab = true;
        /**
         * Flag indicating whether the overlay container should match the width of its parent
         */
        this.pdiOverlayMatchParenWidth = false;
        /**
         * Flag indicating how the user will trigger the overlay display.<br>
         * Options available are 'manual', 'click' and 'hover'.<br>
         * Manual means that overlay functions have to be called directly and no automatic overlay triggering takes place.<br>
         * Click means that the overlay will be displayed on user click on the given target element.<br>
         * Hover means that on mouse hover over the target element the overlay will be displayed.
         */
        this.overlayTriggerMechanism = 'click';
        /**
         * The portal detaching will take place after the given number of millis will pass.<br> Default is <b>0</b>
         */
        this.overlayDetachDelay = 0;
        /**
         * Value in milliseconds on how long the overlay container will be fading before being detached.<br> Default is <b>0</b>
         */
        this.overlayFadeAnimationDuration = 0;
        /**
         * Array of classes used to customize the appearance of the containing panel for the overlay.<br>
         * Default is array with single value <b>pdi-overlay-panel</b> with default PDI styles.
         */
        this.overlayPanelClasses = ['pdi-overlay-panel'];
        /**
         * Name of a class to customize the appearance of the overlay backdrop.<br>
         * Default is <b>pdi-overlay-backdrop</b> that makes the backdrop transparent.
         */
        this.backdropClass = 'pdi-overlay-backdrop';
        /**
         * This flag determines whether the overlay will possess a backdrop or not.<br>
         * Default is <b>true</b>
         */
        // @DocumentInput({
        //   constructor: PdiOverlayDirective,
        //   name: 'hasBackdrop',
        //   type: 'boolean',
        //   description: 'This flag determines whether the overlay will possess a backdrop or not.',
        //   inherited: true,
        //   parentType: 'PdiOverlayDirective',
        // })
        this.hasBackdrop = true;
        // @DocumentInput({
        //   constructor: PdiOverlayDirective,
        //   name: 'overlayScrollStrategyBehavior',
        //   type: 'single-select',
        //   options: ['noop', 'reposition', 'close', 'block'],
        //   description: 'Strategy indicating the manner of handling scroll events.',
        //   inherited: true,
        //   parentType: 'PdiOverlayDirective',
        // })
        this.overlayScrollStrategyBehavior = 'noop';
        /**
         * Flag indicating whether the overlay is bound to a parent component or is global
         */
        // @DocumentInput({
        //   constructor: PdiOverlayDirective,
        //   name: 'isGlobalOverlay',
        //   type: 'boolean',
        //   description: 'Flag indicating whether the overlay is bound to a parent component or is global.',
        //   inherited: true,
        //   parentType: 'PdiOverlayDirective',
        // })
        this.isGlobalOverlay = false;
        /**
         * Flag indicating whether the overlay toggle should occur on element with directive click
         */
        this.enableOverlayToggle = true;
        this.backdropClicked = new EventEmitter();
        this.overlayShowing = new EventEmitter();
        this._preferredPositions = ['bottom'];
        this._overlayVisible = false;
        this._abortFadeAnimation = new Subject();
        this._overlayFadeDelayProcess = false;
    }
    get overlayTemplate() {
        return this._overlayTemplate;
    }
    /**
     * Contents of the menu to be displayed
     */
    set overlayTemplate(overlayTemplate) {
        this._overlayTemplate = overlayTemplate;
        if (this._overlayVisible) {
            this.attachPortal();
        }
    }
    get preferredPositions() {
        return this._preferredPositions;
    }
    /**
     * Where relative to overlay origin to draw the contents.<br>
     * Items at the start of the array have higher priority and will be preferred if viewport allows it.<br>
     * Default is array with single value: <b>bottom</b>
     */
    set preferredPositions(preferredPositions) {
        this._preferredPositions = preferredPositions;
        if (exists(this._overlayRef)) {
            this._overlayRef?.updatePositionStrategy(this.overlayPositionStrategy);
        }
    }
    get overlayPositionStrategy() {
        if (this.isGlobalOverlay) {
            return this.overlay.position().global();
        }
        // return this.overlay.position().connectedTo(this.elementRef, this.constructPositions()[0], this.constructPositions()[0]); // usage because of bugged behavior and auto close not working with 'flexible connected to position'
        return this.overlay.position().flexibleConnectedTo(this.elementRef).withPositions(this.constructPositions());
    }
    static convertPositionToConnectedPosition(pos) {
        const positionAsString = pos.toString();
        const top = positionAsString.includes('top');
        const bottom = positionAsString.includes('bottom');
        const left = positionAsString.includes('left');
        const right = positionAsString.includes('right');
        let originX;
        let originY;
        let overlayX;
        let overlayY;
        originX = right ? 'end' : 'start';
        originY = bottom ? 'bottom' : 'top';
        overlayX = left ? 'end' : 'start';
        overlayY = top ? 'bottom' : 'top';
        return {
            originY,
            originX,
            overlayX,
            overlayY
        };
    }
    focusFirst(keyboardEvent) {
        if (keyboardEvent.code === KeyCode.Enter || (keyboardEvent.code === KeyCode.Space && !this.disableSpaceOverlayTrigger)) {
            this.click();
        }
        if (!this.pdiOverlayFocusOnTab && keyboardEvent.code === KeyCode.Tab) { // if user clicked tab and we are not handling it -> close overlay
            this.closeOverlay();
            return;
        }
        if (this._overlayVisible && (keyboardEvent.code === KeyCode.Down || keyboardEvent.code === KeyCode.Tab)) {
            if (this._componentRef && this._componentRef.instance?.focusFirst && this._overlayVisible) {
                this._componentRef.instance.focusFirst();
                if (keyboardEvent.code === KeyCode.Tab) {
                    keyboardEvent.preventDefault();
                    keyboardEvent.stopPropagation();
                }
            }
        }
    }
    resetDetachTimer() {
        if (this._overlayFadeDelayProcess) {
            if (!this._overlayVisible) {
                this._overlayVisible = true;
                this.overlayShowing.emit(this._overlayVisible);
            }
            this._abortFadeAnimation.next();
            this._overlayFadeDelayProcess = false;
            if (this._overlayRef) {
                this._overlayRef.overlayElement.style.opacity = '1';
            }
        }
    }
    openOverlay() {
        if ((!exists(this._overlayRef) || !this._overlayRef?.hasAttached()) && this.enableOverlayToggle) {
            this.attachPortal();
        }
    }
    closeOverlay(withDelay = true) {
        if ((exists(this._overlayRef) && this._overlayRef?.hasAttached()) && this.enableOverlayToggle) {
            this.detachPortal(withDelay);
        }
    }
    close() {
        this.closeOverlay();
    }
    toggleOverlay() {
        if (this.enableOverlayToggle) {
            this._overlayVisible = !this._overlayVisible;
            if (this._overlayVisible) {
                this.attachPortal();
            }
            else {
                this.detachPortal();
            }
        }
    }
    matchParentWidth() {
        const targetWidth = this.elementRef.nativeElement.getBoundingClientRect().width;
        if (this._overlayRef) {
            this._overlayRef.overlayElement.style.width = `${targetWidth}px`;
        }
    }
    mouseOver() {
        if (this._overlayFadeDelayProcess) {
            this.resetDetachTimer();
        }
        else if (this.enableOverlayToggle && !this._overlayVisible && this.overlayTriggerMechanism === 'hover') {
            this.openOverlay();
        }
    }
    mouseOut() {
        if (this.enableOverlayToggle && this._overlayVisible && this.overlayTriggerMechanism === 'hover') {
            this.closeOverlay();
        }
    }
    click() {
        if (this.enableOverlayToggle && this.overlayTriggerMechanism === 'click') {
            this.toggleOverlay();
        }
    }
    ngOnDestroy() {
        this.close();
        super.ngOnDestroy();
    }
    attachPortal() {
        if (!exists(this._overlayRef)) {
            let scrollStrategy;
            const positionStrategy = this.overlayPositionStrategy;
            if (this.isGlobalOverlay) {
                scrollStrategy = this.overlay.scrollStrategies.block();
            }
            else {
                scrollStrategy = this.overlayScrollStrategy ? this.overlayScrollStrategy : this.overlay.scrollStrategies[this.overlayScrollStrategyBehavior]();
            }
            this._overlayRef = this.overlay.create({
                scrollStrategy,
                hasBackdrop: this.hasBackdrop,
                backdropClass: this.backdropClass,
                panelClass: this.overlayPanelClasses,
                positionStrategy,
            });
            this._overlayRef.backdropClick().pipe(takeUntil(this.ends)).subscribe(() => this.backdropClicked.emit(this));
            this._overlayRef.overlayElement.addEventListener('mouseover', () => this.resetDetachTimer());
            this._overlayRef.overlayElement.addEventListener('mouseout', () => this.mouseOut());
            if (this._modifyOverlayReference) {
                this._modifyOverlayReference(positionStrategy, this._overlayRef);
            }
        }
        this._abortFadeAnimation.next();
        if (this._overlayRef) {
            this._overlayRef.overlayElement.style.opacity = '1';
        }
        if (this._overlayRef?.hasAttached()) {
            this._overlayRef.detach();
        }
        if (this.overlayTemplate) {
            this._portalRef = new TemplatePortal(this.overlayTemplate, this.viewContainerRef, this.overlayTemplateContext);
            this._templateRef = this._overlayRef?.attach(this._portalRef);
            this._overlayVisible = true;
            this.overlayShowing.emit(this._overlayVisible);
        }
        else {
            if (!this.overlayComponent) {
                throw new Error('Either overlay component or template must be defined');
            }
            const injector = new PortalInjector(this.injector, new Map([[OVERLAY_DATA, this.overlayData]]));
            this._portalRef = new ComponentPortal(this.overlayComponent, this.viewContainerRef, injector);
            this._componentRef = this._overlayRef?.attach(this._portalRef);
            this._overlayVisible = true;
            this.overlayShowing.emit(this._overlayVisible);
        }
        if (this.pdiOverlayMatchParenWidth) {
            this.matchParentWidth();
        }
    }
    constructPositions() {
        if (!exists(this.preferredPositions) || this.preferredPositions.length === 0) {
            throw new Error('At least one position *must* be defined in order to display menu');
        }
        return this.preferredPositions.map(PdiOverlayDirective.convertPositionToConnectedPosition);
    }
    detachPortal(withDelay = true) {
        of(undefined).pipe(tap(() => this._overlayFadeDelayProcess = true), delay(withDelay ? this.overlayDetachDelay : 0), // user defined delay
        tap(() => {
            if (this._overlayRef) {
                this._overlayRef.overlayElement.style.transition = `${this.overlayFadeAnimationDuration}ms`;
                this._overlayRef.overlayElement.style.opacity = `0`;
            }
        }), delay(withDelay ? this.overlayFadeAnimationDuration : 0), // style delay to fade the container
        takeUntil(this._abortFadeAnimation)).subscribe({
            next: () => {
                this.internalDetach();
                this._overlayFadeDelayProcess = false;
            }, complete: () => this._overlayFadeDelayProcess = false
        });
    }
    internalDetach() {
        if (this._portalRef && this._portalRef?.isAttached) {
            this._portalRef.detach();
        }
        if (this._overlayRef) {
            if (this._overlayRef.hasAttached()) {
                this._overlayRef.detach();
            }
        }
        this._overlayVisible = false;
        this.overlayShowing.emit(this._overlayVisible);
    }
}
PdiOverlayDirective.genericInputs = [
    'overlayTemplate',
    'preferredPositions',
    'overlayDetachDelay',
    'overlayFadeAnimationDuration',
    'overlayPanelClasses',
    'pdiOverlayFocusOnTab',
    'disableSpaceOverlayTrigger',
    'pdiOverlayMatchParenWidth',
];
PdiOverlayDirective.genericOutputs = ['overlayShowing'];
PdiOverlayDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiOverlayDirective, deps: [{ token: i1$1.Overlay }, { token: i0.Injector }, { token: i0.ElementRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
PdiOverlayDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.1.3", type: PdiOverlayDirective, selector: "[pdiOverlay]", inputs: { disableSpaceOverlayTrigger: "disableSpaceOverlayTrigger", pdiOverlayFocusOnTab: "pdiOverlayFocusOnTab", pdiOverlayMatchParenWidth: "pdiOverlayMatchParenWidth", overlayTriggerMechanism: "overlayTriggerMechanism", overlayTemplateContext: "overlayTemplateContext", overlayDetachDelay: "overlayDetachDelay", overlayFadeAnimationDuration: "overlayFadeAnimationDuration", overlayComponent: "overlayComponent", overlayData: "overlayData", overlayPanelClasses: "overlayPanelClasses", backdropClass: "backdropClass", hasBackdrop: "hasBackdrop", overlayScrollStrategyBehavior: "overlayScrollStrategyBehavior", overlayScrollStrategy: "overlayScrollStrategy", isGlobalOverlay: "isGlobalOverlay", enableOverlayToggle: "enableOverlayToggle", overlayTemplate: "overlayTemplate", preferredPositions: "preferredPositions" }, outputs: { backdropClicked: "backdropClicked", overlayShowing: "overlayShowing" }, host: { listeners: { "keydown": "focusFirst($event)", "mouseover": "mouseOver()", "mouseout": "mouseOut()", "click": "click()" } }, usesInheritance: true, ngImport: i0 });
__decorate([
    DocumentInput({
        constructor: PdiOverlayDirective,
        name: 'disableSpaceOverlayTrigger',
        type: 'boolean',
        description: 'Flag indicating whether hitting the space key will not trigger the overlay to open. Default is <b>false</b> and therefore the overlay will open if overlay target is focused and space key is hit.'
    })
], PdiOverlayDirective.prototype, "disableSpaceOverlayTrigger", void 0);
__decorate([
    DocumentInput({
        constructor: PdiOverlayDirective,
        name: 'pdiOverlayFocusOnTab',
        type: 'boolean',
        description: 'Flag indicating whether to shift focus to the overlay if it is open and user presses Tab key. Default is <b>true</b>',
        inherited: true,
        parentType: 'PdiOverlayDirective',
    })
], PdiOverlayDirective.prototype, "pdiOverlayFocusOnTab", void 0);
__decorate([
    DocumentInput({
        constructor: PdiOverlayDirective,
        name: 'pdiOverlayMatchParenWidth',
        type: 'boolean',
        description: 'Flag indicating whether the overlay container should match the width of its parent',
        inherited: true,
        parentType: 'PdiOverlayDirective',
    })
], PdiOverlayDirective.prototype, "pdiOverlayMatchParenWidth", void 0);
__decorate([
    DocumentInput({
        constructor: PdiOverlayDirective,
        name: 'overlayTriggerMechanism',
        type: 'single-select',
        options: ['manual', 'click', 'hover'],
        description: 'Flag indicating how the user will trigger the overlay display',
        inherited: true,
        parentType: 'PdiOverlayDirective',
    })
], PdiOverlayDirective.prototype, "overlayTriggerMechanism", void 0);
__decorate([
    DocumentInput({
        constructor: PdiOverlayDirective,
        name: 'overlayDetachDelay',
        type: 'number',
        description: 'The portal detaching will take place after the given number of millis will pass.<br> Default is <b>0</b>',
        inherited: true,
        parentType: 'PdiOverlayDirective',
    })
], PdiOverlayDirective.prototype, "overlayDetachDelay", void 0);
__decorate([
    DocumentInput({
        constructor: PdiOverlayDirective,
        name: 'overlayFadeAnimationDuration',
        type: 'number',
        description: 'Value in milliseconds on how long the overlay container will be fading before being detached.<br> Default is <b>0</b>',
        inherited: true,
        parentType: 'PdiOverlayDirective',
    })
], PdiOverlayDirective.prototype, "overlayFadeAnimationDuration", void 0);
__decorate([
    DocumentInput({
        constructor: PdiOverlayDirective,
        name: 'enableOverlayToggle',
        type: 'boolean',
        description: 'Flag indicating whether overlay will show regardless of being triggered.',
        inherited: true,
        parentType: 'PdiOverlayDirective',
    })
], PdiOverlayDirective.prototype, "enableOverlayToggle", void 0);
__decorate([
    DocumentOutput({
        constructor: PdiOverlayDirective,
        name: 'overlayShowing',
        description: 'Event indicating whether the overlay is currently open'
    })
], PdiOverlayDirective.prototype, "overlayShowing", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiOverlayDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[pdiOverlay]'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Overlay }, { type: i0.Injector }, { type: i0.ElementRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { disableSpaceOverlayTrigger: [{
                type: Input
            }], pdiOverlayFocusOnTab: [{
                type: Input
            }], pdiOverlayMatchParenWidth: [{
                type: Input
            }], overlayTriggerMechanism: [{
                type: Input
            }], overlayTemplateContext: [{
                type: Input
            }], overlayDetachDelay: [{
                type: Input
            }], overlayFadeAnimationDuration: [{
                type: Input
            }], overlayComponent: [{
                type: Input
            }], overlayData: [{
                type: Input
            }], overlayPanelClasses: [{
                type: Input
            }], backdropClass: [{
                type: Input
            }], 
        // @DocumentInput({
        //   constructor: PdiOverlayDirective,
        //   name: 'hasBackdrop',
        //   type: 'boolean',
        //   description: 'This flag determines whether the overlay will possess a backdrop or not.',
        //   inherited: true,
        //   parentType: 'PdiOverlayDirective',
        // })
        hasBackdrop: [{
                type: Input
            }], 
        // @DocumentInput({
        //   constructor: PdiOverlayDirective,
        //   name: 'overlayScrollStrategyBehavior',
        //   type: 'single-select',
        //   options: ['noop', 'reposition', 'close', 'block'],
        //   description: 'Strategy indicating the manner of handling scroll events.',
        //   inherited: true,
        //   parentType: 'PdiOverlayDirective',
        // })
        overlayScrollStrategyBehavior: [{
                type: Input
            }], overlayScrollStrategy: [{
                type: Input
            }], 
        // @DocumentInput({
        //   constructor: PdiOverlayDirective,
        //   name: 'isGlobalOverlay',
        //   type: 'boolean',
        //   description: 'Flag indicating whether the overlay is bound to a parent component or is global.',
        //   inherited: true,
        //   parentType: 'PdiOverlayDirective',
        // })
        isGlobalOverlay: [{
                type: Input
            }], enableOverlayToggle: [{
                type: Input
            }], backdropClicked: [{
                type: Output
            }], overlayShowing: [{
                type: Output
            }], overlayTemplate: [{
                type: Input
            }], preferredPositions: [{
                type: Input
            }], focusFirst: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], mouseOver: [{
                type: HostListener,
                args: ['mouseover']
            }], mouseOut: [{
                type: HostListener,
                args: ['mouseout']
            }], click: [{
                type: HostListener,
                args: ['click']
            }] } });

class PdiOverlayModule {
}
PdiOverlayModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiOverlayModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PdiOverlayModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.3", ngImport: i0, type: PdiOverlayModule, declarations: [PdiOverlayDirective], imports: [CommonModule], exports: [PdiOverlayDirective] });
PdiOverlayModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiOverlayModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiOverlayModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PdiOverlayDirective,
                    ],
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        PdiOverlayDirective,
                    ],
                }]
        }] });

function cloneMap(source) {
    const destination = new Map();
    Array.from(source.keys()).forEach(it => {
        destination.set(it, cloneItem(source.get(it)));
    });
    return destination;
}
function cloneArray(source) {
    const retVal = [];
    source.forEach(it => retVal.push(cloneItem(it)));
    return retVal;
}
function cloneItem(source) {
    if (Array.isArray(source)) {
        return cloneArray(source);
    }
    else if (source instanceof Map) {
        return cloneMap(source);
    }
    else if (typeof source === 'object' && source !== null) {
        return cloneObject(source);
    }
    return source; // primitive type that does not pass by reference can be returned directly
}
function cloneObject(source) {
    const destination = {};
    for (const property in source) {
        if (source.hasOwnProperty(property)) {
            if (typeof source[property] === 'object' && source[property] !== null) {
                destination[property] = cloneItem(source[property]);
            }
            else {
                destination[property] = source[property];
            }
        }
    }
    return destination;
}

function upgradeToVersion(version, descMap) {
    const history = Array.from(descMap.entries());
    const latestVersion = descMap.get(history[history.length - 1][0]) ?? [];
    latestVersion.forEach(it => {
        if (it.inputs) {
            it.inputs = it.inputs.filter(that => !that.deprecated);
        }
        if (it.outputs) {
            it.outputs = it.outputs.filter(that => !that.deprecated);
        }
        if (it.properties) {
            it.properties = it.properties.filter(that => !that.deprecated);
        }
    });
    descMap.clear();
    descMap.set(version, latestVersion);
}
function modifyDescriptionClass(forClass, forVersion, descMap, modify) {
    if (!descMap.has(forVersion)) {
        const descs = Array.from(descMap.values());
        const prevDesc = descs[descs.length - 1];
        descMap.set(forVersion, cloneItem(prevDesc));
    }
    let classDescription = descMap.get(forVersion)?.find(it => it.className === forClass);
    if (!classDescription) {
        classDescription = { className: forClass };
        descMap.get(forVersion)?.unshift(classDescription);
    }
    modify(classDescription);
}
function modifyDescription(forVersion, descMap, modify) {
    if (!descMap.has(forVersion)) {
        const descs = Array.from(descMap.values());
        const prevDesc = descs[descs.length - 1];
        descMap.set(forVersion, cloneItem(prevDesc));
    }
    const descriptions = descMap.get(forVersion);
    if (descriptions) {
        modify(descriptions);
    }
}
/**
 * This helper function will update an existing input with given key or create a new one if it does not exist.
 *
 * @param forClass Name of the class for which to update the inputs
 * @param forVersion  Version of the library for which to update the inputs
 * @param descMap Current history map of the component/module
 * @param key Key of the input property to update or add
 * @param description Description to use when updating or adding the input item
 */
function addOrUpdateInput(forClass, forVersion, descMap, key, description) {
    addOrUpdateItem(forClass, forVersion, descMap, key, description, 'inputs');
}
function addOrUpdateOutput(forClass, forVersion, descMap, key, description) {
    addOrUpdateItem(forClass, forVersion, descMap, key, description, 'outputs');
}
function deprecateInput(forClass, forVersion, descMap, key) {
    modifyDescriptionClass(forClass, forVersion, descMap, (desc => {
        const itemsArray = desc['inputs'];
        const item = itemsArray.find(it => it.key === key);
        if (item) {
            item.deprecated = true;
        }
    }));
}
function addOrUpdateItem(forClass, forVersion, descMap, key, description, mode) {
    modifyDescriptionClass(forClass, forVersion, descMap, (desc => {
        const itemsArray = desc[mode];
        const item = itemsArray.find(it => it.key === key);
        if (item) {
            item.description = description;
        }
        else {
            itemsArray.push({ key, description });
        }
    }));
}

const DocsFieldTypes = ['map', 'string', 'single-select', 'object', 'boolean', 'number', 'undefined', 'date', 'array'];

// generated by script in platform-ui-styles/scripts/generateComputedCssColorValues.js utility script
const computedCssColorValues = {
    pdiIndigo: 'rgb(7, 0, 61)',
    pdiIndigoRgba: 'rgba(7, 0, 61, 1)',
    pdiSky: 'rgb(0, 210, 244)',
    pdiSkyRgba: 'rgba(0, 210, 244, 1)',
    pdiPlum: 'rgb(161, 10, 125)',
    pdiPlumRgba: 'rgba(161, 10, 125, 1)',
    pdiOcean: 'rgb(18, 38, 170)',
    pdiOceanRgba: 'rgba(18, 38, 170, 1)',
    pdiIndigoShadow1: 'rgb(7, 0, 61)',
    pdiIndigoShadow1Rgba: 'rgba(7, 0, 61, 15)',
    pdiIndigoShadow2: 'rgb(7, 0, 61)',
    pdiIndigoShadow2Rgba: 'rgba(7, 0, 61, 20)',
    pdiIndigoShadow3: 'rgb(7, 0, 61)',
    pdiIndigoShadow3Rgba: 'rgba(7, 0, 61, 31)',
    pdiIndigoShadow4: 'rgb(7, 0, 61)',
    pdiIndigoShadow4Rgba: 'rgba(7, 0, 61, 61)',
    pdiProductBaseWhite: 'rgb(255, 255, 255)',
    pdiProductBaseWhiteRgba: 'rgba(255, 255, 255, 1)',
    pdiProductBackgroundGray: 'rgb(247, 249, 251)',
    pdiProductBackgroundGrayRgba: 'rgba(247, 249, 251, 1)',
    pdiCoolGray: 'rgb(217, 216, 214)',
    pdiCoolGrayRgba: 'rgba(217, 216, 214, 1)',
    pdiProductDarkSlate: 'rgb(104, 111, 111)',
    pdiProductDarkSlateRgba: 'rgba(104, 111, 111, 1)',
    pdiGranite: 'rgb(50, 62, 72)',
    pdiGraniteRgba: 'rgba(50, 62, 72, 1)',
    pdiIndigoFooter: 'rgb(18, 32, 72)',
    pdiIndigoFooterRgba: 'rgba(18, 32, 72, 1)',
    pdiProductSuccess: 'rgb(0, 199, 89)',
    pdiProductSuccessRgba: 'rgba(0, 199, 89, 1)',
    pdiProductAlert: 'rgb(199, 15, 43)',
    pdiProductAlertRgba: 'rgba(199, 15, 43, 1)',
    pdiProductAction: 'rgb(15, 101, 240)',
    pdiProductActionRgba: 'rgba(15, 101, 240, 1)',
    pdiSienna: 'rgb(255, 81, 0)',
    pdiSiennaRgba: 'rgba(255, 81, 0, 1)',
    pdiSunshine: 'rgb(255, 183, 27)',
    pdiSunshineRgba: 'rgba(255, 183, 27, 1)',
    pdiSoftGreyDividers: 'rgb(226, 226, 226)',
    pdiSoftGreyDividersRgba: 'rgba(226, 226, 226, 1)',
    pdiSoftLightBlue: 'rgb(91, 182, 255)',
    pdiSoftLightBlueRgba: 'rgba(91, 182, 255, 1)',
    pdiTintGreen: 'rgb(242, 251, 251)',
    pdiTintGreenRgba: 'rgba(242, 251, 251, 1)',
    pdiLightTeal: 'rgb(0, 181, 173)',
    pdiLightTealRgba: 'rgba(0, 181, 173, 13)',
    pdiLightRed: 'rgb(199, 15, 43)',
    pdiLightRedRgba: 'rgba(199, 15, 43, 13)',
    pdiLightYellow: 'rgb(251, 189, 8)',
    pdiLightYellowRgba: 'rgba(251, 189, 8, 13)',
    pdiShadowT8: 'rgb(6, 64, 158)',
    pdiShadowT8Rgba: 'rgba(6, 64, 158, 20)',
    pdiShadowT12: 'rgb(6, 64, 158)',
    pdiShadowT12Rgba: 'rgba(6, 64, 158, 31)',
    pdiShadowT16: 'rgb(6, 64, 158)',
    pdiShadowT16Rgba: 'rgba(6, 64, 158, 61)',
    pdiShadowBox: 'rgb(6, 64, 158)',
    pdiShadowBoxRgba: 'rgba(6, 64, 158, 31)',
    pdiBlue: 'rgb(6, 64, 158)',
    pdiBlueRgba: 'rgba(6, 64, 158, 1)',
    pdiPrimary: 'rgb(6, 64, 158)',
    pdiPrimaryRgba: 'rgba(6, 64, 158, 1)',
    pdiBrandPrimary: 'rgb(6, 64, 158)',
    pdiBrandPrimaryRgba: 'rgba(6, 64, 158, 1)',
    pdiLightBlue: 'rgb(6, 64, 158)',
    pdiLightBlueRgba: 'rgba(6, 64, 158, 13)',
    pdiLightActionBlue: 'rgb(6, 64, 158)',
    pdiLightActionBlueRgba: 'rgba(6, 64, 158, 13)',
    pdiBrandPrimaryTransparent: 'rgb(6, 64, 158)',
    pdiBrandPrimaryTransparentRgba: 'rgba(6, 64, 158, 13)',
    pdiActionBlue: 'rgb(15, 101, 240)',
    pdiActionBlueRgba: 'rgba(15, 101, 240, 1)',
    pdiActionPrimary: 'rgb(15, 101, 240)',
    pdiActionPrimaryRgba: 'rgba(15, 101, 240, 1)',
    pdiSoftBlue: 'rgb(139, 183, 253)',
    pdiSoftBlueRgba: 'rgba(139, 183, 253, 1)',
    pdiActionSecondary: 'rgb(139, 183, 253)',
    pdiActionSecondaryRgba: 'rgba(139, 183, 253, 1)',
    pdiTintBlue: 'rgb(242, 247, 255)',
    pdiTintBlueRgba: 'rgba(242, 247, 255, 1)',
    pdiTintActionPrimary: 'rgb(242, 247, 255)',
    pdiTintActionPrimaryRgba: 'rgba(242, 247, 255, 1)',
    pdiTintBlueMid: 'rgb(216, 226, 240)',
    pdiTintBlueMidRgba: 'rgba(216, 226, 240, 1)',
    pdiTintActionSecondary: 'rgb(219, 232, 253)',
    pdiTintActionSecondaryRgba: 'rgba(219, 232, 253, 1)',
    pdiTintTeal: 'rgb(242, 251, 251)',
    pdiTintTealRgba: 'rgba(242, 251, 251, 1)',
    pdiTintSuccess: 'rgb(242, 251, 251)',
    pdiTintSuccessRgba: 'rgba(242, 251, 251, 1)',
    pdiTintYellow: 'rgb(255, 248, 229)',
    pdiTintYellowRgba: 'rgba(255, 248, 229, 1)',
    pdiTintWarning: 'rgb(255, 248, 229)',
    pdiTintWarningRgba: 'rgba(255, 248, 229, 1)',
    pdiTintRed: 'rgb(254, 242, 244)',
    pdiTintRedRgba: 'rgba(254, 242, 244, 1)',
    pdiTintAlert: 'rgb(254, 242, 244)',
    pdiTintAlertRgba: 'rgba(254, 242, 244, 1)',
    pdiSuccess: 'rgb(0, 115, 20)',
    pdiSuccessRgba: 'rgba(0, 115, 20, 1)',
    pdiSuccessPrimary: 'rgb(0, 115, 20)',
    pdiSuccessPrimaryRgba: 'rgba(0, 115, 20, 1)',
    pdiTeal: 'rgb(0, 181, 173)',
    pdiTealRgba: 'rgba(0, 181, 173, 1)',
    pdiGreen: 'rgb(0, 115, 20)',
    pdiGreenRgba: 'rgba(0, 115, 20, 1)',
    pdiSuccessSecondary: 'rgb(0, 181, 173)',
    pdiSuccessSecondaryRgba: 'rgba(0, 181, 173, 1)',
    pdiRed: 'rgb(199, 15, 43)',
    pdiRedRgba: 'rgba(199, 15, 43, 1)',
    pdiAlert: 'rgb(199, 15, 43)',
    pdiAlertRgba: 'rgba(199, 15, 43, 1)',
    pdiAlertPrimary: 'rgb(199, 15, 43)',
    pdiAlertPrimaryRgba: 'rgba(199, 15, 43, 1)',
    pdiOrange: 'rgb(236, 103, 14)',
    pdiOrangeRgba: 'rgba(236, 103, 14, 1)',
    pdiWarningPrimary: 'rgb(236, 103, 14)',
    pdiWarningPrimaryRgba: 'rgba(236, 103, 14, 1)',
    pdiWarning: 'rgb(236, 103, 14)',
    pdiWarningRgba: 'rgba(236, 103, 14, 1)',
    pdiYellow: 'rgb(251, 189, 8)',
    pdiYellowRgba: 'rgba(251, 189, 8, 1)',
    pdiWarningSecondary: 'rgb(251, 189, 8)',
    pdiWarningSecondaryRgba: 'rgba(251, 189, 8, 1)',
    pdiStatus: 'rgb(251, 189, 8)',
    pdiStatusRgba: 'rgba(251, 189, 8, 1)',
    pdiWhite: 'rgb(255, 255, 255)',
    pdiWhiteRgba: 'rgba(255, 255, 255, 1)',
    pdiGhostWhite: 'rgb(249, 249, 251)',
    pdiGhostWhiteRgba: 'rgba(249, 249, 251, 1)',
    pdiBaseWhite: 'rgb(255, 255, 255)',
    pdiBaseWhiteRgba: 'rgba(255, 255, 255, 1)',
    pdiLightGray: 'rgb(247, 247, 251)',
    pdiLightGrayRgba: 'rgba(247, 247, 251, 1)',
    pdiLightGrey: 'rgb(247, 247, 251)',
    pdiLightGreyRgba: 'rgba(247, 247, 251, 1)',
    pdiMediumGray: 'rgb(210, 210, 215)',
    pdiMediumGrayRgba: 'rgba(210, 210, 215, 1)',
    pdiMediumGrey: 'rgb(210, 210, 215)',
    pdiMediumGreyRgba: 'rgba(210, 210, 215, 1)',
    pdiDarkGray: 'rgb(113, 113, 113)',
    pdiDarkGrayRgba: 'rgba(113, 113, 113, 1)',
    pdiDarkGrey: 'rgb(113, 113, 113)',
    pdiDarkGreyRgba: 'rgba(113, 113, 113, 1)',
    pdiGrey: 'rgb(210, 210, 215)',
    pdiGreyRgba: 'rgba(210, 210, 215, 1)',
    pdiGray: 'rgb(210, 210, 215)',
    pdiGrayRgba: 'rgba(210, 210, 215, 1)',
    pdiBlack: 'rgb(0, 0, 0)',
    pdiBlackRgba: 'rgba(0, 0, 0, 1)',
    pdiSoftBlack: 'rgb(51, 51, 51)',
    pdiSoftBlackRgba: 'rgba(51, 51, 51, 1)',
    pdiBoxShadow: 'rgba(6, 64, 158, 0.12) 0px 4px 12px 0px',
    pdiTransparent: 'rgb(0, 0, 0)',
    pdiTransparentRgba: 'rgba(0, 0, 0, 0)'
};
const originalCssHexColorValues = {
    pdiIndigo: '#07003D',
    pdiSky: '#00D2F4',
    pdiPlum: '#A10A7D',
    pdiOcean: '#1226AA',
    pdiIndigoShadow1: '#07003D0F',
    pdiIndigoShadow2: '#07003D14',
    pdiIndigoShadow3: '#07003D1F',
    pdiIndigoShadow4: '#07003D3D',
    pdiProductBaseWhite: '#FFFFFF',
    pdiProductBackgroundGray: '#F7F9FB',
    pdiCoolGray: '#D9D8D6',
    pdiProductDarkSlate: '#686F6F',
    pdiGranite: '#323E48',
    pdiIndigoFooter: '#122048',
    pdiProductSuccess: '#00C759',
    pdiProductAlert: '#C70F2B',
    pdiProductAction: '#0F65F0',
    pdiSienna: '#FF5100',
    pdiSunshine: '#FFB71B',
    pdiSoftGreyDividers: '#E2E2E2',
    pdiSoftLightBlue: '#5BB6FF',
    pdiTintGreen: '#f2fbfb',
    pdiLightTeal: '#00b5ad0d',
    pdiLightRed: '#C70F2B0d',
    pdiLightYellow: '#fbbd080d',
    pdiShadowT8: '#06409E14',
    pdiShadowT12: '#06409E1f',
    pdiShadowT16: '#06409E3D',
    pdiShadowBox: '#06409E1F',
    pdiBlue: '#06409e',
    pdiPrimary: '#06409e',
    pdiBrandPrimary: '#06409e',
    pdiLightBlue: '#06409E0d',
    pdiLightActionBlue: '#06409E0d',
    pdiBrandPrimaryTransparent: '#06409E0d',
    pdiActionBlue: '#0F65F0',
    pdiActionPrimary: '#0F65F0',
    pdiSoftBlue: '#8BB7FD',
    pdiActionSecondary: '#8BB7FD',
    pdiTintBlue: '#F2F7FF',
    pdiTintActionPrimary: '#F2F7FF',
    pdiTintBlueMid: '#D8E2F0',
    pdiTintActionSecondary: '#DBE8FD',
    pdiTintTeal: '#F2FBFB',
    pdiTintSuccess: '#F2FBFB',
    pdiTintYellow: '#FFF8E5',
    pdiTintWarning: '#FFF8E5',
    pdiTintRed: '#FEF2F4',
    pdiTintAlert: '#FEF2F4',
    pdiSuccess: '#007314',
    pdiSuccessPrimary: '#007314',
    pdiTeal: '#00B5AD',
    pdiGreen: '#007314',
    pdiSuccessSecondary: '#00B5AD',
    pdiRed: '#C70F2B',
    pdiAlert: '#C70F2B',
    pdiAlertPrimary: '#C70F2B',
    pdiOrange: '#EC670E',
    pdiWarningPrimary: '#EC670E',
    pdiWarning: '#EC670E',
    pdiYellow: '#FBBD08',
    pdiWarningSecondary: '#FBBD08',
    pdiStatus: '#FBBD08',
    pdiWhite: '#ffffff',
    pdiGhostWhite: '#F9F9FB',
    pdiBaseWhite: '#ffffff',
    pdiLightGray: '#f7f7fb',
    pdiLightGrey: '#f7f7fb',
    pdiMediumGray: '#d2d2d7',
    pdiMediumGrey: '#d2d2d7',
    pdiDarkGray: '#717171',
    pdiDarkGrey: '#717171',
    pdiGrey: '#d2d2d7',
    pdiGray: '#d2d2d7',
    pdiBlack: '#000000',
    pdiSoftBlack: '#333333'
};
/*
export type PdiColor = 'pdiIndigo' |
 'pdiSky' |
 'pdiPlum' |
 'pdiOcean' |
 'pdiIndigoShadow1' |
 'pdiIndigoShadow2' |
 'pdiIndigoShadow3' |
 'pdiIndigoShadow4' |
 'pdiProductBaseWhite' |
 'pdiProductBackgroundGray' |
 'pdiCoolGray' |
 'pdiProductDarkSlate' |
 'pdiGranite' |
 'pdiIndigoFooter' |
 'pdiProductSuccess' |
 'pdiProductAlert' |
 'pdiProductAction' |
 'pdiSienna' |
 'pdiSunshine' |
 'pdiSoftGreyDividers' |
 'pdiSoftLightBlue' |
 'pdiTintGreen' |
 'pdiLightTeal' |
 'pdiLightRed' |
 'pdiLightYellow' |
 'pdiShadowT8' |
 'pdiShadowT12' |
 'pdiShadowT16' |
 'pdiShadowBox' |
 'pdiBlue' |
 'pdiPrimary' |
 'pdiBrandPrimary' |
 'pdiLightBlue' |
 'pdiLightActionBlue' |
 'pdiBrandPrimaryTransparent' |
 'pdiActionBlue' |
 'pdiActionPrimary' |
 'pdiSoftBlue' |
 'pdiActionSecondary' |
 'pdiTintBlue' |
 'pdiTintActionPrimary' |
 'pdiTintBlueMid' |
 'pdiTintActionSecondary' |
 'pdiTintTeal' |
 'pdiTintSuccess' |
 'pdiTintYellow' |
 'pdiTintWarning' |
 'pdiTintRed' |
 'pdiTintAlert' |
 'pdiSuccess' |
 'pdiSuccessPrimary' |
 'pdiTeal' |
 'pdiGreen' |
 'pdiSuccessSecondary' |
 'pdiRed' |
 'pdiAlert' |
 'pdiAlertPrimary' |
 'pdiOrange' |
 'pdiWarningPrimary' |
 'pdiWarning' |
 'pdiYellow' |
 'pdiWarningSecondary' |
 'pdiStatus' |
 'pdiWhite' |
 'pdiGhostWhite' |
 'pdiBaseWhite' |
 'pdiLightGray' |
 'pdiLightGrey' |
 'pdiMediumGray' |
 'pdiMediumGrey' |
 'pdiDarkGray' |
 'pdiDarkGrey' |
 'pdiGrey' |
 'pdiGray' |
 'pdiBlack' |
 'pdiSoftBlack';
*/
var PdiColor;
(function (PdiColor) {
    PdiColor["pdiIndigo"] = "pdiIndigo";
    PdiColor["pdiSky"] = "pdiSky";
    PdiColor["pdiPlum"] = "pdiPlum";
    PdiColor["pdiOcean"] = "pdiOcean";
    PdiColor["pdiIndigoShadow1"] = "pdiIndigoShadow1";
    PdiColor["pdiIndigoShadow2"] = "pdiIndigoShadow2";
    PdiColor["pdiIndigoShadow3"] = "pdiIndigoShadow3";
    PdiColor["pdiIndigoShadow4"] = "pdiIndigoShadow4";
    PdiColor["pdiProductBaseWhite"] = "pdiProductBaseWhite";
    PdiColor["pdiProductBackgroundGray"] = "pdiProductBackgroundGray";
    PdiColor["pdiCoolGray"] = "pdiCoolGray";
    PdiColor["pdiProductDarkSlate"] = "pdiProductDarkSlate";
    PdiColor["pdiGranite"] = "pdiGranite";
    PdiColor["pdiIndigoFooter"] = "pdiIndigoFooter";
    PdiColor["pdiProductSuccess"] = "pdiProductSuccess";
    PdiColor["pdiProductAlert"] = "pdiProductAlert";
    PdiColor["pdiProductAction"] = "pdiProductAction";
    PdiColor["pdiSienna"] = "pdiSienna";
    PdiColor["pdiSunshine"] = "pdiSunshine";
    PdiColor["pdiSoftGreyDividers"] = "pdiSoftGreyDividers";
    PdiColor["pdiSoftLightBlue"] = "pdiSoftLightBlue";
    PdiColor["pdiTintGreen"] = "pdiTintGreen";
    PdiColor["pdiLightTeal"] = "pdiLightTeal";
    PdiColor["pdiLightRed"] = "pdiLightRed";
    PdiColor["pdiLightYellow"] = "pdiLightYellow";
    PdiColor["pdiShadowT8"] = "pdiShadowT8";
    PdiColor["pdiShadowT12"] = "pdiShadowT12";
    PdiColor["pdiShadowT16"] = "pdiShadowT16";
    PdiColor["pdiShadowBox"] = "pdiShadowBox";
    PdiColor["pdiBlue"] = "pdiBlue";
    PdiColor["pdiPrimary"] = "pdiPrimary";
    PdiColor["pdiBrandPrimary"] = "pdiBrandPrimary";
    PdiColor["pdiLightBlue"] = "pdiLightBlue";
    PdiColor["pdiLightActionBlue"] = "pdiLightActionBlue";
    PdiColor["pdiBrandPrimaryTransparent"] = "pdiBrandPrimaryTransparent";
    PdiColor["pdiActionBlue"] = "pdiActionBlue";
    PdiColor["pdiActionPrimary"] = "pdiActionPrimary";
    PdiColor["pdiSoftBlue"] = "pdiSoftBlue";
    PdiColor["pdiActionSecondary"] = "pdiActionSecondary";
    PdiColor["pdiTintBlue"] = "pdiTintBlue";
    PdiColor["pdiTintActionPrimary"] = "pdiTintActionPrimary";
    PdiColor["pdiTintBlueMid"] = "pdiTintBlueMid";
    PdiColor["pdiTintActionSecondary"] = "pdiTintActionSecondary";
    PdiColor["pdiTintTeal"] = "pdiTintTeal";
    PdiColor["pdiTintSuccess"] = "pdiTintSuccess";
    PdiColor["pdiTintYellow"] = "pdiTintYellow";
    PdiColor["pdiTintWarning"] = "pdiTintWarning";
    PdiColor["pdiTintRed"] = "pdiTintRed";
    PdiColor["pdiTintAlert"] = "pdiTintAlert";
    PdiColor["pdiSuccess"] = "pdiSuccess";
    PdiColor["pdiSuccessPrimary"] = "pdiSuccessPrimary";
    PdiColor["pdiTeal"] = "pdiTeal";
    PdiColor["pdiGreen"] = "pdiGreen";
    PdiColor["pdiSuccessSecondary"] = "pdiSuccessSecondary";
    PdiColor["pdiRed"] = "pdiRed";
    PdiColor["pdiAlert"] = "pdiAlert";
    PdiColor["pdiAlertPrimary"] = "pdiAlertPrimary";
    PdiColor["pdiOrange"] = "pdiOrange";
    PdiColor["pdiWarningPrimary"] = "pdiWarningPrimary";
    PdiColor["pdiWarning"] = "pdiWarning";
    PdiColor["pdiYellow"] = "pdiYellow";
    PdiColor["pdiWarningSecondary"] = "pdiWarningSecondary";
    PdiColor["pdiStatus"] = "pdiStatus";
    PdiColor["pdiWhite"] = "pdiWhite";
    PdiColor["pdiGhostWhite"] = "pdiGhostWhite";
    PdiColor["pdiBaseWhite"] = "pdiBaseWhite";
    PdiColor["pdiLightGray"] = "pdiLightGray";
    PdiColor["pdiLightGrey"] = "pdiLightGrey";
    PdiColor["pdiMediumGray"] = "pdiMediumGray";
    PdiColor["pdiMediumGrey"] = "pdiMediumGrey";
    PdiColor["pdiDarkGray"] = "pdiDarkGray";
    PdiColor["pdiDarkGrey"] = "pdiDarkGrey";
    PdiColor["pdiGrey"] = "pdiGrey";
    PdiColor["pdiGray"] = "pdiGray";
    PdiColor["pdiBlack"] = "pdiBlack";
    PdiColor["pdiSoftBlack"] = "pdiSoftBlack";
})(PdiColor || (PdiColor = {}));
const computedFontFamilyValues = {
    primary: /["']DM Sans['"].*/,
    secondary: /['"]Hind Siliguri['"].*/
};

const MOBILE_BREAKPOINT = 768;
class PdiWindowResizeService {
    constructor() {
        this._resizeEvent = new Subject();
        this._resizeHandler = (event) => {
            this._resizeEvent.next(event.target.innerWidth);
        };
        this._originalWindowDimensions = window.innerWidth;
        window.addEventListener('resize', this._resizeHandler);
    }
    /**
     * Function returns an observable that fires a number based on current window width and breakpoint defined by the user.
     * @param breakpoint optinal numeric value that represents pixels of window width at which to trigger the breaking events, default is mobile/tablet breakpoint with value of 768
     * @param debounceMs optional numeric value that represents the milliseconds to debounce window resize events with, default value is 200
     * @return 0 if window width is equal to breakpoint, -1 if current window width is less than the breakpoint and 1 if current window width is greater than the breakpoint
     */
    windowResizeObservableAtBreakpoint(breakpoint = MOBILE_BREAKPOINT, debounceMs = 200) {
        return this._resizeEvent.asObservable().pipe(startWith(window.innerWidth), debounceTime(debounceMs), distinctUntilChanged(), map(windowWidth => {
            return windowWidth < breakpoint ? -1 : (windowWidth === breakpoint ? 0 : 1); // -1 if width is less than breakpoint, 0 if they are equal, 1 if width is greater than breakpoint
        }), distinctUntilChanged());
    }
    ngOnDestroy() {
        window.removeEventListener('resize', this._resizeHandler);
    }
}
PdiWindowResizeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiWindowResizeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
PdiWindowResizeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiWindowResizeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.3", ngImport: i0, type: PdiWindowResizeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Generated bundle index. Do not edit.
 */

export { CoreRadioComponent, Destructible, DocsFieldTypes, DocumentComponent, DocumentInput, DocumentOutput, HIERARCHY_MAP, INPUTS_LIST_NAME, KeyCode, MOBILE_BREAKPOINT, OUTPUTS_LIST_NAME, OVERLAY_DATA, PdiColor, PdiCoreCheckmarkComponent, PdiCoreComponentsModule, PdiDocumentationContainer, PdiInputModule, PdiKeydownEnterDirective, PdiOverlayDirective, PdiOverlayModule, PdiWindowResizeService, Themable, addOrUpdateInput, addOrUpdateOutput, cloneArray, cloneItem, cloneMap, cloneObject, computedCssColorValues, computedFontFamilyValues, deprecateInput, exists, modifyDescription, modifyDescriptionClass, originalCssHexColorValues, upgradeToVersion };
